/**
 * Curated plugin packages: the bundled catalog, what one pinned version
 * includes, its shareable Markdown export, and the user's installations.
 * Errors render inline in Settings, so every request is silent.
 */
import { apiRequest, apiRequestText } from "./client";

interface ApiResponse<T> {
  status: "success";
  data: T;
}

const BASE = "/api/v1/plugins";

export interface PluginSummary {
  slug: string;
  version: string;
  name: string;
  description: string;
  /** Bundled with Denker, or one of the user's saved templates. */
  source: "bundled" | "private";
  installed: boolean;
  installed_version: string | null;
}

/** A read-only call that checks an app works, and what it should return. */
export interface PluginAppTestCall {
  action: string;
  arguments: Record<string, string | number | boolean | null>;
  expects: string;
}

/** How the app is read when it has no integration or is not connected. */
export interface PluginAppAlternative {
  kind: "api_key" | "computer_use";
  how: string;
}

export interface PluginApp {
  key: string;
  name: string;
  /** Null only for an app without an integration; `alternative` is then set. */
  toolkit_slug: string | null;
  required: boolean;
  purpose: string;
  test_call: PluginAppTestCall | null;
  alternative: PluginAppAlternative | null;
  /** Connect can work: Composio manages the toolkit's credentials or Denker registered its own. */
  connectable: boolean;
}

export interface PluginSkill {
  kind: "agent" | "work";
  ref: string;
  required: boolean;
  purpose: string;
}

export interface PluginDocLayout {
  sections: Array<{ heading: string; purpose: string }>;
  per_run: boolean;
}

export interface PluginDatasetLayout {
  collection: string;
}

export interface PluginChartLayout {
  kind: "line" | "bar" | "number";
  data: string;
  label_field: string;
  value_field: string;
  aggregate: "count" | "sum" | "avg" | "min" | "max" | "last";
  limit: number | null;
  unit: string | null;
  date_field: string | null;
}

/** A per-run report card: a ring, a headline and summary, three tiles, next steps. */
export interface PluginReportLayout {
  per_run: boolean;
  ring_of: string;
  /** How many checks each row of `ring_of` counts for (e.g. 3 engines per question). */
  ring_per_row?: number;
  /** The caption under the ring: what it counts (e.g. "AI answers that name you"). */
  ring_label?: string | null;
  headline: string;
  summary: string;
  tiles: string[];
  next: string[];
}

export interface PluginTaskboardLayout {
  view: "board" | "list" | "calendar";
}

/** Each frame type with its layout; an HTML frame has one only as a report card. */
export type PluginFrame = {
  key: string;
  title: string;
  /** Job keys that fill this frame. */
  filled_by: string[];
} & (
  | { type: "html"; layout: PluginReportLayout | null }
  | { type: "doc"; layout: PluginDocLayout }
  | { type: "dataset"; layout: PluginDatasetLayout }
  | { type: "chart"; layout: PluginChartLayout }
  | { type: "taskboard"; layout: PluginTaskboardLayout }
);

export interface PluginPilot {
  jobs: string[];
}

export interface PluginTaskSchedule {
  /** daily, weekdays, weekly, biweekly or monthly. */
  rrule: string;
  rrule_time: string;
  rrule_day: number | null;
  timezone: string;
  is_active: boolean;
}

export interface PluginTask {
  key: string;
  title: string;
  role: "coordinator" | "worker" | "topic";
  assign_to_agent: boolean;
  blocked_by: string[];
  schedule: PluginTaskSchedule | null;
  /** Runs on its schedule, once at Start, or when a card is picked. */
  trigger: "schedule" | "start" | "pick";
  summary: string;
  /** Worker task keys this job hands work to. */
  helpers: string[];
  /** The taskboard frame key a `pick` task's cards live on. */
  board: string | null;
}

export interface PluginDetail extends PluginSummary {
  digest: string;
  /** True once project setup can apply this package (slice C). */
  project_setup: boolean;
  apps: PluginApp[];
  skills: PluginSkill[];
  frames: PluginFrame[];
  tasks: PluginTask[];
  pilot: PluginPilot;
}

export interface PluginInstallation {
  id: string;
  slug: string;
  version: string;
  digest: string;
  name: string;
  description: string;
  /** The pinned version is still bundled. */
  available: boolean;
  latest_version: string | null;
  upgrade_available: boolean;
  installed_at: string;
  updated_at: string;
}

const packagePath = (slug: string, version: string) =>
  `${BASE}/catalog/${encodeURIComponent(slug)}/${encodeURIComponent(version)}/`;
const installationPath = (slug: string) =>
  `${BASE}/installations/${encodeURIComponent(slug)}/`;

export async function listPluginCatalog(): Promise<PluginSummary[]> {
  const response = await apiRequest<ApiResponse<PluginSummary[]>>(
    `${BASE}/catalog/`,
    { silent: true },
  );
  return response.data;
}

export async function getPluginDetail(
  slug: string,
  version: string,
): Promise<PluginDetail> {
  const response = await apiRequest<ApiResponse<PluginDetail>>(
    packagePath(slug, version),
    { silent: true },
  );
  return response.data;
}

/** The package's shareable Markdown recipe. */
export function exportPlugin(slug: string, version: string): Promise<string> {
  return apiRequestText(`${packagePath(slug, version)}export/`, {
    silent: true,
  });
}

export async function listPluginInstallations(): Promise<PluginInstallation[]> {
  const response = await apiRequest<ApiResponse<PluginInstallation[]>>(
    `${BASE}/installations/`,
    { silent: true },
  );
  return response.data;
}

export async function upgradePlugin(
  slug: string,
  version: string,
): Promise<PluginInstallation> {
  const response = await apiRequest<ApiResponse<PluginInstallation>>(
    installationPath(slug),
    { method: "PATCH", body: { version }, silent: true },
  );
  return response.data;
}

export async function removePlugin(slug: string): Promise<void> {
  await apiRequest(installationPath(slug), { method: "DELETE", silent: true });
}
